import { IColumn, SelectionMode } from "@fluentui/react";
import { RootState } from "app/store";
import CustomDetailList from "components/controls/CustomDetailList";
import { DefaultGridConfig, UpdateColumnByConfig } from "components/controls/CustomDetailList/CustomDetailList";
import { ICustomDetailListConfiguration } from "components/controls/CustomDetailList/ICustomDetailListConfiguration";
import { IGridItem } from "components/controls/CustomDetailList/IGridItem";
import { filterPredicateColumnPropertyName, filterPredicateColumnPropertyString, onRenderColumnBenefitRef, onRenderColumnPropertyName, onRenderColumnPropertyString, onRenderColumnRef, sortPredicateColumnPropertyName, sortPredicateColumnPropertyString } from "components/controls/Grid";
import { AppLinkTo } from "global/Application";
import { updateCustomDetailListConfiguration } from "global/Reducer/statusSlice";
import { ArrayToLookup } from "global/Utility/TSFunctions";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import * as FAM from "model/FlexibleAttributeModel";
import { ICheckList } from "model/ICheckList";
import { IExtendedColumn } from "model/IExtendedColumn";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

interface IPlanGridProps {
    plans?: FAM.IPlan[];
    checkListByPlanId?: Record<string, ICheckList>
}

const CheckListGrid: React.FunctionComponent<IPlanGridProps> = (props) => {

    const history = useHistory();

    const state = useSelector((state: RootState) => state);

    const plans = props.plans ?? state.data.json.plans;

    const storageId = `plan.grid`;

    const dispatch = useDispatch();

    const scopeGridConfig = () => {
        const u = state.status.gridConfigs[storageId];
        if (u)
            return u;

        const t = DefaultGridConfig();
        // t.sort = [{ column: { key: 'attributeSetName' }, isSortedDescending: false }];

        return t;
    }

    const [gridConfig, setGridConfig] = useState<ICustomDetailListConfiguration>(scopeGridConfig());
    const gridConfigRef = useRef(gridConfig);

    // check if it has changed since mounting
    if (gridConfig !== gridConfigRef.current) {
        // console.log("render:grid config updated", gridConfig);

        gridConfigRef.current = gridConfig;

        // snapshot for bind back
        dispatch(updateCustomDetailListConfiguration({ id: storageId, gridConfig: structuredClone(gridConfig) }));
    }


    const onColumnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, column: IColumn) => {
        setGridConfig(t => {

            const gridConfig: ICustomDetailListConfiguration = { ...t };

            gridConfig.sort = [{
                column: { key: column.key },
                isSortedDescending: !t?.sort?.[0]?.isSortedDescending ?? false,
            }];

            return gridConfig;
        });
    };

    const columns = useMemo(() => {

        const columns: IExtendedColumn[] = [];

        {
            const column: IExtendedColumn = {
                key: 'checklistRef',
                fieldName: 'checklistRef',
                name: 'Check List',
                minWidth: 400,
                onRender: onRenderColumnRef
            }

            column.onColumnClick = onColumnClick;
            column.sortPredicate = sortPredicateColumnPropertyName;
            column.filterPredicate = filterPredicateColumnPropertyName;
            UpdateColumnByConfig(column, gridConfig);

            columns.push(column);
        }



        {
            const column: IExtendedColumn = {
                key: 'checklistStatus',
                fieldName: 'checklistStatus',
                name: 'Status',
                minWidth: 150,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyString
            column.filterPredicate = filterPredicateColumnPropertyString;

            columns.push(column);
        }


        {
            const column: IExtendedColumn = {
                key: 'billingOnly',
                fieldName: 'billingOnly',
                name: 'Billing Only',
                minWidth: 80,
                onRender: onRenderColumnPropertyString
            }
            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyString
            column.filterPredicate = filterPredicateColumnPropertyString;
            columns.push(column);
        }

        {
            const column: IExtendedColumn = {
                key: 'planRef',
                fieldName: 'planRef',
                name: 'Plan',
                minWidth: 400,
                onRender: onRenderColumnRef
            }

            column.onColumnClick = onColumnClick;
            column.sortPredicate = sortPredicateColumnPropertyName;
            column.filterPredicate = filterPredicateColumnPropertyName;
            UpdateColumnByConfig(column, gridConfig);

            columns.push(column);
        }


        {
            const column: IExtendedColumn = {
                key: 'grandFathered',
                fieldName: 'grandFathered',
                name: 'GF/NGF Type',
                minWidth: 150,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyString
            column.filterPredicate = filterPredicateColumnPropertyString;


            columns.push(column);
        }

        {
            const column: IExtendedColumn = {
                key: 'fundingType',
                fieldName: 'fundingType',
                name: 'Funding Type',
                minWidth: 250,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyString
            column.filterPredicate = filterPredicateColumnPropertyString;


            columns.push(column);
        }

        {
            const column: IExtendedColumn = {
                key: 'feesAndPremiumRef',
                fieldName: 'feesAndPremiumRef',
                name: 'Fees And Premium',
                minWidth: 250,
                onRender: onRenderColumnRef,
            }

            column.onColumnClick = onColumnClick;
            UpdateColumnByConfig(column, gridConfig);
            column.sortPredicate = sortPredicateColumnPropertyName
            column.filterPredicate = filterPredicateColumnPropertyName;

            columns.push(column);
        }


        // order is defined by client:
        'Ancillary,Medical,Pharmacy,Dental,Vision,HRA,HSA,FSA,COBRA'.split(',').forEach(cat => {
            {
                const column: IExtendedColumn = {
                    key: `${cat}BenefitRef`,
                    fieldName: `${cat}BenefitRef`,
                    name: `${cat} Benefit`,
                    minWidth: 200,
                    onRender: onRenderColumnBenefitRef,
                }

                column.onColumnClick = onColumnClick;
                UpdateColumnByConfig(column, gridConfig);
                column.sortPredicate = sortPredicateColumnPropertyName
                column.filterPredicate = filterPredicateColumnPropertyName;

                columns.push(column);
            }

        });

        return columns;
    }, [gridConfig]);

    const [items, itemsPaged] = useMemo(() => {
        const items: IGridItem<FAM.IPlan>[] = [];

        const benefits = state.data.json.benefits;
        const benefitsLookup = ArrayToLookup(benefits);

        const gridConfigMappedColumns = {
            filterColumns: gridConfig.filter.map(q => columns?.find(w => w.key === q.column.key)),
            sortColumns: gridConfig.sort.map(q => columns?.find(w => w.key === q.column.key)),
        }

        plans.forEach((ePlan, ix) => {

            const planBenefits = ePlan.benefitRefs.map(eB => benefitsLookup[eB.id]);

            // skip deactivated records/only show deactivated records if permitted at group config properties level
            if ((ePlan.deactivated ?? false) !== state.data.json.showInActives?.value)
                return;

            const checklist = props.checkListByPlanId?.[ePlan.id];
            const item: IGridItem<FAM.IPlan> = {
                _index: ix,
                key: ePlan.id!,
                data: ePlan,
                name: ePlan.name,
                checklistRef: { id: checklist?.bcbs_emchecklist.bcbs_emchecklistid, name: ePlan.name },
                checklistStatus: (checklist?.bcbs_emchecklist as any)?.['statuscode@OData.Community.Display.V1.FormattedValue'],
                billingOnly: ePlan.billingOnlyPlan ? "Billing Only" : undefined,
                planRef: { id: ePlan.id, name: ePlan.name },
                fundingType: ePlan.fundingType?.name,
                grandFathered: ePlan.grandFathered?.name,
                feesAndPremiumRef: ePlan.feesAndPremiumsRef
                    ? { id: ePlan.feesAndPremiumsRef.id, name: ePlan.feesAndPremiumsRef.name }
                    : undefined,
                plan: ePlan
            };

            planBenefits.forEach(eachPlanBenefit => {
                const category = eachPlanBenefit.bcbs_name!;
                item[`${category}BenefitRef`] = { id: eachPlanBenefit.id, name: eachPlanBenefit.name };
            })

            // Filtering :
            let include = true;
            if (gridConfig && gridConfig.filter && gridConfig.filter.length > 0) {

                gridConfig.filter.forEach((filter, ix) => {
                    const column = gridConfigMappedColumns.filterColumns[ix];

                    if (column) {
                        const predicate = column.filterPredicate;
                        if (predicate)
                            include = include && predicate(item, filter.value, column);
                    }
                });

            }

            if (include)
                items.push(item);
        });

        // items.sort((a, b) => { return (a.data?.name ?? '') > (b.data?.name ?? '') ? 1 : -1 });
        if (gridConfig && gridConfig.sort && gridConfig.sort.length > 0)
            items.sort((a, b) => {

                let ret = 0;

                gridConfig.sort?.forEach((sort, ix) => {
                    const column = gridConfigMappedColumns.sortColumns[ix];

                    // TODO why was this needed?
                    if (!column)
                        return;

                    const predicate = column.sortPredicate;
                    const isSortedDescending = sort.isSortedDescending ?? false;

                    if (predicate)
                        ret = isSortedDescending
                            ? predicate(a, b, undefined, column) * -1
                            : predicate(a, b, undefined, column);
                });

                return ret;

            });

        const itemsPaged = items.slice((gridConfig.page - 1) * gridConfig.pageSize, gridConfig.page * gridConfig.pageSize);

        return [items, itemsPaged];
    }, [plans, gridConfig]);

    return <CustomDetailList
        columns={columns}
        items={items}
        itemsPaged={itemsPaged}
        selectionMode={SelectionMode.none}
        // onRenderRow={(props, defaultRender) => {
        //     props?.item
        //     return <div>
        //         {defaultRender?.({ ...props!, styles: { root: { background: 'lightgray' } } })}
        //     </div>
        // }}
        defaultGridConfig={gridConfig}
        quickSearchColumnKey={'planRef'}
        gridConfig={[gridConfig, setGridConfig]}
        onItemInvoked={(item: IGridItem<FAM.IPlan>, index, event) => {
            if (item.data) {
                const checklist = props.checkListByPlanId?.[item.data.id];
                history.push(AppLinkTo(state, { id: checklist?.bcbs_emchecklist.bcbs_emchecklistid!, type: 'checkListWithPlan' }))

            }
        }}
    />;
}
export default CheckListGrid;